import gql from 'graphql-tag'
import { connect } from 'react-redux'
import { compose } from 'recompose'

import { selectors as UISelectors } from 'data/ui'
import {
  noteAttrsForSubscriptionsFragment,
  notesDeletedSubscription,
  notesArchivedSubscription,
  notesUnarchivedSubscription,
  notesMovedSubscription,
} from 'remote/notes'

import { dataFetched, dataRemoved, makesSubscriptions } from 'data/graphql'
import {
  notesArchived,
  notesDeleted,
  notesMoved,
  notesUnarchived,
  noteCreated,
  acknowledgeFileImportSuccess,
  acknowledgeFileImportFailed,
} from 'data/notes/actions'

const noteUpdateSubscription = gql`
  subscription noteUpdated($organizationId: ID!) {
    noteUpdated(organizationId: $organizationId) {
      ...noteAttrsForSubscriptions
    }
  }
  ${noteAttrsForSubscriptionsFragment}
`

const noteTemplateCreatedSubscription = gql`
  subscription noteTemplateCreated($organizationId: ID!) {
    noteTemplateCreated(organizationId: $organizationId) {
      ...noteAttrsForSubscriptions
    }
  }
  ${noteAttrsForSubscriptionsFragment}
`

const noteActivityUpdateSubscription = gql`
  subscription noteActivityUpdated($organizationId: ID!) {
    noteActivityUpdated(organizationId: $organizationId) {
      id
      __typename
      activityCount
      lastActivityAt
      lastActivityBy
      lastActivityType
    }
  }
`

const noteCreationSubscription = gql`
  subscription noteCreated($organizationId: ID!) {
    noteCreated(organizationId: $organizationId) {
      ...noteAttrsForSubscriptions
    }
  }
  ${noteAttrsForSubscriptionsFragment}
`

const fileImportDoneSubscription = gql`
  subscription fileImportFinished($organizationId: ID!) {
    fileImportFinished(organizationId: $organizationId) {
      __typename
      channelName
      channelId
    }
  }
`

const fileImportFailedSubscription = gql`
  subscription fileImportFailed($organizationId: ID!) {
    fileImportFailed(organizationId: $organizationId) {
      __typename
      fileName
      channelName
    }
  }
`

const noteTemplateDeletedSubscription = gql`
  subscription noteTemplateDeleted($organizationId: ID!) {
    noteTemplateDeleted(organizationId: $organizationId) {
      __typename
      id
    }
  }
`

const pickOrganizationId = ({ organizationId }) => ({ organizationId })

const providesNotesSubscriptions = compose(
  connect(
    state => ({
      organizationId: UISelectors.getActiveOrganizationId(state),
    }),
    {
      noteCreated,
      notesArchived,
      notesUnarchived,
      notesDeleted,
      notesMoved,
      dataFetched,
      dataRemoved,
      acknowledgeFileImportSuccess,
      acknowledgeFileImportFailed,
      noteTemplateCreated(template) {
        return {
          type: 'noteTemplateCreated',
          payload: template,
        }
      },
      noteTemplateDeleted(template) {
        return {
          type: 'noteTemplateDeleted',
          payload: template,
        }
      },
    }
  ),
  makesSubscriptions({
    query: noteUpdateSubscription,
    mapPropsToVariables: pickOrganizationId,
    getOnResultFromProps: ({ dataFetched }) => ({ result, meta }) => {
      dataFetched(result.data.noteUpdated, meta)
    },
  }),
  makesSubscriptions({
    query: noteActivityUpdateSubscription,
    mapPropsToVariables: pickOrganizationId,
    getOnResultFromProps: ({ dataFetched }) => ({ result, meta }) => {
      dataFetched(result.data.noteActivityUpdated, meta)
    },
  }),
  makesSubscriptions({
    query: noteCreationSubscription,
    mapPropsToVariables: pickOrganizationId,
    getOnResultFromProps: ({ noteCreated }) => ({ result, meta }) => {
      noteCreated(result.data.noteCreated, meta)
    },
  }),
  makesSubscriptions({
    query: fileImportDoneSubscription,
    mapPropsToVariables: pickOrganizationId,
    getOnResultFromProps: ({ acknowledgeFileImportSuccess }) => ({
      result,
    }) => {
      const { channelId, channelName } = result.data.fileImportFinished
      acknowledgeFileImportSuccess({ channelId, channelName })
    },
  }),
  makesSubscriptions({
    query: fileImportFailedSubscription,
    mapPropsToVariables: pickOrganizationId,
    getOnResultFromProps: ({ acknowledgeFileImportFailed }) => ({ result }) => {
      const { channelName, fileName } = result.data.fileImportFailed
      acknowledgeFileImportFailed(fileName, channelName)
    },
  }),
  makesSubscriptions({
    query: notesDeletedSubscription,
    mapPropsToVariables: pickOrganizationId,
    getOnResultFromProps: ({ notesDeleted }) => ({ result, meta }) => {
      notesDeleted({ noteIds: result.data.notesDeleted.ids })
    },
  }),
  makesSubscriptions({
    query: notesArchivedSubscription,
    mapPropsToVariables: pickOrganizationId,
    getOnResultFromProps: ({ notesArchived }) => ({ result, meta }) => {
      notesArchived({ noteIds: result.data.notesArchived.ids })
    },
  }),
  makesSubscriptions({
    query: notesUnarchivedSubscription,
    mapPropsToVariables: pickOrganizationId,
    getOnResultFromProps: ({ notesUnarchived }) => ({ result, meta }) => {
      notesUnarchived({ noteIds: result.data.notesUnarchived.ids })
    },
  }),
  makesSubscriptions({
    query: notesMovedSubscription,
    mapPropsToVariables: pickOrganizationId,
    getOnResultFromProps: ({ notesMoved }) => ({ result, meta }) => {
      const { ids, channelId, parentNoteId } = result.data.notesMoved
      notesMoved({ noteIds: ids, channelId, parentNoteId })
    },
  }),
  makesSubscriptions({
    query: noteTemplateCreatedSubscription,
    mapPropsToVariables: pickOrganizationId,
    getOnResultFromProps: ({ noteTemplateCreated }) => ({ result, meta }) => {
      noteTemplateCreated(result.data.noteTemplateCreated, meta)
    },
  }),
  makesSubscriptions({
    query: noteTemplateDeletedSubscription,
    mapPropsToVariables: pickOrganizationId,
    getOnResultFromProps: ({ noteTemplateDeleted }) => ({ result, meta }) => {
      noteTemplateDeleted(result.data.noteTemplateDeleted, meta)
    },
  })
)

export default providesNotesSubscriptions
